// API 인터셉터 — 데모 모드가 켜져 있고 시연 계정 요청이면 번들로 응답한다.
//
// services/API.js 의 싱글턴 메서드를 감싼다. 데모가 꺼져 있거나 다른 매장 id 면
// 원래 메서드를 그대로 호출한다 (다른 계정에는 어떤 영향도 없다).
// 날짜 인자는 무시한다 — 번들은 데모 월 한 달 고정(생성기 옵션 a).
import { DEMO_STORE_ID, isDemoActive } from "./config";
import {
  applyTablePage,
  deepClone,
  emptyEnvelope,
  filterRowsByName,
  loadEvents,
  loadJson,
  loadManifest,
  pickSections,
  variantKey,
} from "./bundle";
import { V2_ITEMS } from "../helpers/reportV2";

const BASIS_ITEMS = ["salesSummary", "product", "storeList"];
const DEFAULT_MODE = { salesSummary: "daily", product: "product" };
const TABLE_ITEMS = { salesSummary: "table", storeList: "table", product: "table" };

const parseOptions = (options) => {
  if (!options) return {};
  if (typeof options === "string") {
    try {
      return JSON.parse(options);
    } catch (e) {
      return {};
    }
  }
  return options;
};

const isDemoLocation = (manifest, locationId) =>
  !!locationId && (manifest?.locations || []).some((l) => l.id === locationId);

// ---- getPeriodReport ---------------------------------------------------------
async function demoPeriodReport(args) {
  const [, item, , , locationId, basis, origin, , options] = args;
  const manifest = await loadManifest();
  const opts = parseOptions(options);
  // 멤버십은 서버가 locationId 를 쓰지 않아 번들도 hq 1회만 있다
  const scope = item !== "membership" && isDemoLocation(manifest, locationId) ? locationId : "hq";
  // 쿠폰 드릴다운 변형은 topCoupons 섹션만 담고 있다 — 다른 섹션 요청엔 기본 봉투
  const wantsTopCoupons = !Array.isArray(opts.sections) || opts.sections.includes("topCoupons");
  if (!V2_ITEMS.includes(item)) {
    // v1 아이템 (customerSummary·marketingCustomer·taxRefundItems …)
    const file = await loadJson(`v1/${scope}.json`);
    const v = file?.variants?.[variantKey(item, scope)];
    return deepClone(v === undefined ? null : v);
  }
  const file = await loadJson(`report/${item}/${scope}.json`);
  const variants = file?.variants || {};
  const norm = {
    basis: BASIS_ITEMS.includes(item) ? (["pay", "sales"].includes(basis) ? basis : "pay") : null,
    origin: item === "discount" ? origin || null : null,
    mode: item in DEFAULT_MODE ? opts.mode || DEFAULT_MODE[item] : null,
    grain: item === "membership" ? opts.grain || "month" : null,
    dimension: null,
    token: null,
    couponId: item === "discount" && wantsTopCoupons ? opts.couponId || null : null,
  };
  if (item === "product") {
    if (norm.mode === "product") norm.dimension = opts.dimension || "location";
    else if (norm.mode === "customer") {
      norm.dimension = opts.dimension || null;
      norm.token = opts.token || null;
    }
  }
  // 폴백 순서 — 미캡처 쿠폰 드릴다운은 기본 봉투 → 미캡처 rank 차원은 location → 미캡처 영업일 기준은 결제일
  const candidates = [norm];
  if (norm.couponId) candidates.push({ ...norm, couponId: null });
  if (norm.mode === "product" && norm.dimension !== "location") {
    candidates.push(...candidates.map((c) => ({ ...c, dimension: "location" })));
  }
  if (norm.basis === "sales") candidates.push(...candidates.map((c) => ({ ...c, basis: "pay" })));
  const keys = candidates.map((c) => variantKey(item, scope, c));
  let env = null;
  for (const k of keys) {
    if (variants[k]) {
      env = deepClone(variants[k]);
      break;
    }
  }
  if (!env) return emptyEnvelope(item);
  const data = env.data;
  if (!data) return env;

  // 표 검색·카테고리 필터 (서버 어셈블러 순서: 필터 → 페이지)
  if (item === "product" && data.table?.rows) {
    if (opts.q) data.table.rows = filterRowsByName(data.table.rows, opts.q, ["productName"]);
    if (Array.isArray(opts.categoryIds) && opts.categoryIds.length && norm.mode === "product") {
      const allow = new Set(
        (manifest?.productCategories || [])
          .filter((pc) => opts.categoryIds.includes(pc.categoryId))
          .map((pc) => pc.productId),
      );
      data.table.rows = data.table.rows.filter((r) => allow.has(r.productId));
    }
  }
  if (item === "storeList" && data.table?.rows && opts.q) {
    data.table.rows = filterRowsByName(data.table.rows, opts.q, ["locationName"]);
  }
  if (item === "taxRefund" && data.locations?.rows && opts.q) {
    data.locations.rows = filterRowsByName(data.locations.rows, opts.q, ["locationName"]);
  }
  if (item === "discount" && opts.q) {
    if (data.byLocation?.rows) data.byLocation.rows = filterRowsByName(data.byLocation.rows, opts.q, ["locationName"]);
    if (data.usage?.rows) data.usage.rows = filterRowsByName(data.usage.rows, opts.q, ["name"]);
  }
  // 페이지·정렬 (§11-1 서버 페이지드 표)
  if (TABLE_ITEMS[item] && data[TABLE_ITEMS[item]]?.rows) {
    env.meta = env.meta || {};
    env.meta.page = applyTablePage(data, item, TABLE_ITEMS[item], opts.page, opts.size, opts.sort);
  }
  // 분리 로딩 include-list
  env.data = pickSections(data, opts.sections);
  return env;
}

// ---- 마감 / 캠페인 / 이벤트 / 쿠폰 --------------------------------------------
const inRange = (iso, start, end) => {
  const t = new Date(iso).getTime();
  const s = start ? new Date(start).getTime() : -Infinity;
  const e = end ? new Date(end).getTime() : Infinity;
  return t >= s && t <= e;
};

async function demoSalesHistories(locationId, start, end) {
  const rows = await loadJson("endsales/histories.json");
  return rows.filter(
    (h) => (!locationId || h.locationId === locationId) && inRange(h.startedAt, start, end),
  );
}

async function demoCampaigns(page, size, filters) {
  const list = await loadJson("campaign/list.json");
  const f = {};
  (Array.isArray(filters) ? filters : []).forEach((x) => {
    if (x && x.key) f[x.key] = x.value;
  });
  let items = (list.items || []).filter((c) => {
    if (f.campaignId && f.campaignId !== c.id) return false;
    if (f.type === "auto" && c.type !== "auto") return false;
    if (f.type && f.type !== "auto" && c.type === "auto") return false;
    if (f.method && f.method !== c.method) return false;
    return true;
  });
  const p = Math.max(1, Number(page) || 1);
  const s = Math.max(1, Number(size) || 50);
  return { count: items.length, items: deepClone(items.slice((p - 1) * s, p * s)) };
}

async function demoCampaignReport(start, end, filters) {
  const f = (Array.isArray(filters) ? filters : []).find((x) => x?.key === "campaignId" && x.value);
  if (f) {
    try {
      return deepClone(await loadJson(`campaign/report/${f.value}.json`));
    } catch (e) {
      // 미캡처 캠페인 — 전체로
    }
  }
  const days = start && end ? (new Date(end) - new Date(start)) / 86400000 : 31;
  return deepClone(await loadJson(days > 45 ? "campaign/report/all_2m.json" : "campaign/report/all.json"));
}

const slice = (data, page, size) => {
  const items = data?.items || [];
  if (!page || !size) return { count: data?.count ?? items.length, items: deepClone(items) };
  const p = Math.max(1, Number(page));
  const s = Math.max(1, Number(size));
  return { count: data?.count ?? items.length, items: deepClone(items.slice((p - 1) * s, p * s)) };
};

// ---- 설치 -----------------------------------------------------------------
/**
 * @param {object} api - services/API 싱글턴
 */
export function installDemoInterceptor(api) {
  const wrap = (name, handler) => {
    const orig = api[name];
    if (typeof orig !== "function") return;
    api[name] = function demoWrapped(...args) {
      if (!isDemoActive()) return orig.apply(api, args);
      let out;
      try {
        out = handler(args);
      } catch (e) {
        out = undefined;
      }
      if (out === undefined) return orig.apply(api, args);
      return Promise.resolve(out);
    };
  };
  const forStore = (storeId, fn) => (storeId === DEMO_STORE_ID ? fn() : undefined);

  wrap("getPeriodReport", (args) => forStore(args[0], () => demoPeriodReport(args)));
  wrap("getPeriodReportExport", (args) =>
    forStore(args[0], () =>
      Promise.reject(new Error("데모 데이터 모드에서는 다운로드를 지원하지 않습니다.")),
    ),
  );
  wrap("getSalesHistoriesByStore", ([storeId, start, end]) =>
    forStore(storeId, () => demoSalesHistories(null, start, end)),
  );
  wrap("getSalesHistoriesByLocation", ([locationId, start, end]) =>
    loadManifest().then((m) =>
      isDemoLocation(m, locationId) ? demoSalesHistories(locationId, start, end) : undefined,
    ).then((r) => (r === undefined ? api.__demoOrig.getSalesHistoriesByLocation(locationId, start, end) : r)),
  );
  wrap("getMessageCampaigns", ([storeId, page, size, filters]) =>
    forStore(storeId, () => demoCampaigns(page, size, filters)),
  );
  wrap("getMessageCampaignReport", ([storeId, start, end, filters]) =>
    forStore(storeId, () => demoCampaignReport(start, end, filters)),
  );
  wrap("getCampaignWithReport", ([id]) =>
    loadJson("campaign/list.json").then((list) => {
      const c = (list.items || []).find((x) => x.id === id);
      return c ? deepClone(c) : api.__demoOrig.getCampaignWithReport(id);
    }),
  );
  wrap("getCustomerEventsByStore", ([storeId]) => forStore(storeId, () => loadEvents().then(deepClone)));
  wrap("getCustomerEventReport", ([eventId, start, end]) =>
    loadEvents().then((list) =>
      (list.items || []).some((e) => e.id === eventId)
        ? loadJson(`event/report/${eventId}.json`).then(deepClone)
        : api.__demoOrig.getCustomerEventReport(eventId, start, end),
    ),
  );
  wrap("getCustomerEventList", ([eventId, item, page, size]) =>
    loadEvents().then((list) =>
      (list.items || []).some((e) => e.id === eventId)
        ? loadJson(`event/list/${eventId}_${item}.json`).then((d) => slice(d, page, size))
        : api.__demoOrig.getCustomerEventList(eventId, item, page, size),
    ),
  );
  wrap("getCoupons", ([storeId]) => forStore(storeId, () => loadJson("coupons.json").then(deepClone)));
  // 원본 참조 (폴백용)
  api.__demoOrig = api.__demoOrig || {};
  ["getSalesHistoriesByLocation", "getCampaignWithReport", "getCustomerEventReport", "getCustomerEventList"].forEach(
    (n) => {
      if (!api.__demoOrig[n]) api.__demoOrig[n] = Object.getPrototypeOf(api)[n].bind(api);
    },
  );
}
